/**
 * Availability & pricing endpoints for Mews Booking Engine API
 *
 * Phase 3: Availability & Pricing Integration
 *
 * Implements:
 * - getAvailability()          - Room availability & rate pricing
 * - getReservationsPricing()   - Detailed reservation pricing for a room/occupancy
 * - getServicesAvailability()  - Add-on/service availability
 * - getServicesPricing()       - Add-on/service pricing
 *
 * All operations use HTTP POST with JSON bodies, via the shared API client.
 *
 * ---
 * Optional `CurrencyCode` on Mews requests (display-currency / multi-currency audit):
 *
 * | Call | `CurrencyCode` on request | Notes |
 * |------|---------------------------|--------|
 * | `hotels/getAvailability` | **Omitted** | Sending it collapses `Price.Total` / `TotalAmount` to one currency; omit for full maps. |
 * | `reservations/getPricing` | None set here | Not used in this module’s request body. |
 * | `services/getAvailability` | Omitted | No currency filter in body. |
 * | `services/getPricing` | **Only if `currencyCode` passed** | Omit for multi-currency maps; pass e.g. `"CHF"` when a single currency is required. |
 *
 * Final checkout pricing uses `reservations/price` in {@link ./reservation-groups.ts} — **CHF** by default.
 */

import { getApiClient } from './client';
import type {
  ApiResponse,
  AvailabilityResponse,
  ReservationsPricingResponse,
  ServicesAvailabilityResponse,
  ServicesPricingResponse,
} from './types';

/**
 * Mews OccupancyData item
 *
 * This matches the structure produced by convertToOccupancies().
 */
export interface OccupancyData {
  AgeCategoryId: string;
  PersonCount: number;
}

/**
 * Occupancy structure for getReservationsPricing
 * Each occupancy contains an OccupancyData array
 */
export interface Occupancy {
  OccupancyData: OccupancyData[];
}

/**
 * Request parameters for getAvailability()
 *
 * See MEWS_API_IMPLEMENTATION_PLAN Phase 3.1.
 */
export interface GetAvailabilityParams {
  /** Inclusive start of stay (ISO 8601 UTC) */
  startUtc: string;
  /** Exclusive end of stay (ISO 8601 UTC) */
  endUtc: string;
  /** OccupancyData array from convertToOccupancies() */
  occupancyData: OccupancyData[];
  /** Optional override for HotelId (defaults to env/config) */
  hotelId?: string;
  /** Optional override for ConfigurationId (defaults to env/config) */
  configurationId?: string;
  /** Optional filter for specific room categories */
  categoryIds?: string[];
  /** Optional filter for specific products/add-ons */
  productIds?: string[];
  /** Optional voucher / promo code */
  voucherCode?: string;
  /** Optional availability block (packages, promotions) */
  availabilityBlockId?: string;
  /** Optional language code (e.g. "en-US") */
  languageCode?: string;
  /** Optional culture code */
  cultureCode?: string;
  /** Optional rate IDs to request; if supported by the API, ensures pricing for these rates is returned */
  rateIds?: string[];
}

/**
 * Checks room availability & pricing for a given date range and occupancy.
 *
 * Endpoint: POST /api/distributor/v1/hotels/getAvailability
 *
 * **Multi-currency:** `CurrencyCode` is intentionally **not** sent. Mews narrows `Price.Total` /
 * `TotalAmount` to a single currency when `CurrencyCode` is set; omitting it preserves per-ISO maps
 * for display-only currency selection (see project docs under `docs/room booking page/`).
 *
 * @example
 * ```ts
 * const occupancies = convertToOccupancies(adults, children, ageCategories);
 * const response = await getAvailability({
 *   startUtc,
 *   endUtc,
 *   occupancyData: occupancies,
 * });
 * ```
 */
export async function getAvailability(
  params: GetAvailabilityParams
): Promise<ApiResponse<AvailabilityResponse>> {
  const client = getApiClient();

  const hotelId = params.hotelId ?? client.getHotelId();
  const configurationId = params.configurationId ?? client.getConfigurationId();

  if (!configurationId) {
    return {
      error: {
        Message:
          'ConfigurationId is required for availability. Set VITE_MEWS_CONFIGURATION_ID or pass configurationId.',
        Code: 'MISSING_CONFIGURATION_ID',
      },
      status: 400,
    };
  }

  if (!hotelId) {
    return {
      error: {
        Message:
          'HotelId is required for availability. Set VITE_MEWS_HOTEL_ID or pass hotelId.',
        Code: 'MISSING_HOTEL_ID',
      },
      status: 400,
    };
  }

  const {
    startUtc,
    endUtc,
    occupancyData,
    categoryIds,
    productIds,
    voucherCode,
    availabilityBlockId,
    languageCode,
    cultureCode,
    rateIds,
  } = params;

  const requestBody: Record<string, unknown> = {
    Client: client.getClientIdentifier(),
    ConfigurationId: configurationId,
    HotelId: hotelId,
    StartUtc: startUtc,
    EndUtc: endUtc,
    OccupancyData: occupancyData,
  };

  if (categoryIds && categoryIds.length > 0) {
    requestBody.CategoryIds = categoryIds;
  }
  if (productIds && productIds.length > 0) {
    requestBody.ProductIds = productIds;
  }
  if (rateIds && rateIds.length > 0) {
    requestBody.RateIds = rateIds;
  }
  if (voucherCode) {
    requestBody.VoucherCode = voucherCode;
  }
  if (availabilityBlockId) {
    requestBody.AvailabilityBlockId = availabilityBlockId;
  }
  if (languageCode) {
    requestBody.LanguageCode = languageCode;
  }
  if (cultureCode) {
    requestBody.CultureCode = cultureCode;
  }

  if (import.meta.env.DEV) {
    console.log('[Availability] Request details:', {
      endpoint: '/api/distributor/v1/hotels/getAvailability',
      requestBody,
    });
  }

  return getApiClient().post<AvailabilityResponse>(
    '/api/distributor/v1/hotels/getAvailability',
    requestBody
  );
}

/**
 * Request parameters for getReservationsPricing()
 *
 * See MEWS_API_IMPLEMENTATION_PLAN Phase 3.2.
 */
export interface GetReservationsPricingParams {
  /** Inclusive start of stay (ISO 8601 UTC) */
  startUtc: string;
  /** Exclusive end of stay (ISO 8601 UTC) */
  endUtc: string;
  /** Single room category to price */
  roomCategoryId: string;
  /** One or more occupancy combinations - each Occupancy object contains an OccupancyData array */
  occupancies: Occupancy[];
  /** Optional override for HotelId (defaults to env/config) */
  hotelId?: string;
  /** Optional product/add-on IDs to include */
  productIds?: string[];
  /** Optional voucher / promo code */
  voucherCode?: string;
  /** Optional availability block ID */
  availabilityBlockId?: string;
}

/**
 * Gets pricing quotation for a specific room category & occupancy.
 *
 * Endpoint: POST /api/distributor/v1/reservations/getPricing
 */
export async function getReservationsPricing(
  params: GetReservationsPricingParams
): Promise<ApiResponse<ReservationsPricingResponse>> {
  const client = getApiClient();
  const hotelId = params.hotelId ?? client.getHotelId();

  if (!hotelId) {
    return {
      error: {
        Message:
          'HotelId is required for reservation pricing. Set VITE_MEWS_HOTEL_ID or pass hotelId.',
        Code: 'MISSING_HOTEL_ID',
      },
      status: 400,
    };
  }

  const {
    startUtc,
    endUtc,
    roomCategoryId,
    occupancies,
    productIds,
    voucherCode,
    availabilityBlockId,
  } = params;

  const requestBody: Record<string, unknown> = {
    Client: client.getClientIdentifier(),
    HotelId: hotelId,
    StartUtc: startUtc,
    EndUtc: endUtc,
    RoomCategoryId: roomCategoryId,
    Occupancies: occupancies,
  };

  if (productIds && productIds.length > 0) {
    requestBody.ProductIds = productIds;
  }
  if (voucherCode) {
    requestBody.VoucherCode = voucherCode;
  }
  if (availabilityBlockId) {
    requestBody.AvailabilityBlockId = availabilityBlockId;
  }

  if (import.meta.env.DEV) {
    console.log('[Reservations Pricing] Request details:', {
      endpoint: '/api/distributor/v1/reservations/getPricing',
      requestBody,
    });
  }

  return getApiClient().post<ReservationsPricingResponse>(
    '/api/distributor/v1/reservations/getPricing',
    requestBody
  );
}

/**
 * Request parameters for getServicesAvailability()
 *
 * See MEWS_API_IMPLEMENTATION_PLAN Phase 3.4.
 */
export interface GetServicesAvailabilityParams {
  /** Inclusive start of date range (ISO 8601 UTC) */
  startUtc: string;
  /** Exclusive end of date range (ISO 8601 UTC) */
  endUtc: string;
  /** ServiceId from configuration/hotels response */
  serviceId: string;
  /** Optional EnterpriseId (defaults to env hotelId) */
  enterpriseId?: string;
  /** Optional category filters */
  categoryIds?: string[];
  /** Optional language code */
  languageCode?: string;
}

/**
 * Gets availability for add-on/services for a given date range.
 *
 * Endpoint: POST /api/distributor/v1/services/getAvailability
 */
export async function getServicesAvailability(
  params: GetServicesAvailabilityParams
): Promise<ApiResponse<ServicesAvailabilityResponse>> {
  const client = getApiClient();
  const enterpriseId = params.enterpriseId ?? client.getHotelId();

  if (!enterpriseId) {
    return {
      error: {
        Message:
          'EnterpriseId is required for services availability. Use VITE_MEWS_HOTEL_ID or pass enterpriseId.',
        Code: 'MISSING_ENTERPRISE_ID',
      },
      status: 400,
    };
  }

  const { startUtc, endUtc, serviceId, categoryIds, languageCode } = params;

  const requestBody: Record<string, unknown> = {
    Client: client.getClientIdentifier(),
    EnterpriseId: enterpriseId,
    ServiceId: serviceId,
    StartUtc: startUtc,
    EndUtc: endUtc,
  };

  if (categoryIds && categoryIds.length > 0) {
    requestBody.CategoryIds = categoryIds;
  }
  if (languageCode) {
    requestBody.LanguageCode = languageCode;
  }

  if (import.meta.env.DEV) {
    console.log('[Services Availability] Request details:', {
      endpoint: '/api/distributor/v1/services/getAvailability',
      requestBody,
    });
  }

  return getApiClient().post<ServicesAvailabilityResponse>(
    '/api/distributor/v1/services/getAvailability',
    requestBody
  );
}

/**
 * Request parameters for getServicesPricing()
 *
 * See MEWS_API_IMPLEMENTATION_PLAN Phase 3.4.
 */
export interface GetServicesPricingParams {
  /** Inclusive start of date range (ISO 8601 UTC) */
  startUtc: string;
  /** Exclusive end of date range (ISO 8601 UTC) */
  endUtc: string;
  /** ServiceId from configuration/hotels response */
  serviceId: string;
  /** Optional EnterpriseId (defaults to env hotelId) */
  enterpriseId?: string;
  /** Optional category filters */
  categoryIds?: string[];
  /** Optional rate filters */
  rateIds?: string[];
  /** Optional language code */
  languageCode?: string;
  /**
   * If set, sent as `CurrencyCode` (single-currency response). If omitted, `CurrencyCode` is not
   * sent so the API can return multi-currency `Pricing` maps when supported.
   */
  currencyCode?: string;
  /** Optional OccupancyData array */
  occupancyData?: OccupancyData[];
}

/**
 * Gets pricing for add-on/services for a given date range.
 *
 * Endpoint: POST /api/distributor/v1/services/getPricing
 */
export async function getServicesPricing(
  params: GetServicesPricingParams
): Promise<ApiResponse<ServicesPricingResponse>> {
  const client = getApiClient();
  const enterpriseId = params.enterpriseId ?? client.getHotelId();

  if (!enterpriseId) {
    return {
      error: {
        Message:
          'EnterpriseId is required for services pricing. Use VITE_MEWS_HOTEL_ID or pass enterpriseId.',
        Code: 'MISSING_ENTERPRISE_ID',
      },
      status: 400,
    };
  }

  const {
    startUtc,
    endUtc,
    serviceId,
    categoryIds,
    rateIds,
    languageCode,
    currencyCode,
    occupancyData,
  } = params;

  const requestBody: Record<string, unknown> = {
    Client: client.getClientIdentifier(),
    EnterpriseId: enterpriseId,
    ServiceId: serviceId,
    StartUtc: startUtc,
    EndUtc: endUtc,
  };

  if (categoryIds && categoryIds.length > 0) {
    requestBody.CategoryIds = categoryIds;
  }
  if (rateIds && rateIds.length > 0) {
    requestBody.RateIds = rateIds;
  }
  if (languageCode) {
    requestBody.LanguageCode = languageCode;
  }
  if (currencyCode !== undefined && currencyCode !== '') {
    requestBody.CurrencyCode = currencyCode;
  }
  if (occupancyData && occupancyData.length > 0) {
    requestBody.OccupancyData = occupancyData;
  }

  if (import.meta.env.DEV) {
    console.log('[Services Pricing] Request details:', {
      endpoint: '/api/distributor/v1/services/getPricing',
      requestBody,
    });
  }

  return getApiClient().post<ServicesPricingResponse>(
    '/api/distributor/v1/services/getPricing',
    requestBody
  );
}

